import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { debounceTime, filter, firstValueFrom, map, pairwise, startWith, Subject, takeUntil } from 'rxjs';
import { OrganizationContact, Tag } from 'src/app/models';
import { GenericResource, IGenericListOptions } from 'src/app/resources';
import { MasterApiService } from 'src/app/services/master-api.service';

@Component({
    selector: 'app-global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.sass'],
    standalone: false
})
export class GlobalSearchComponent implements OnDestroy {

  @ViewChild('wrapper') wrapper?: ElementRef;
  inputHidden: boolean;  // indica si mostrem només el botó de cerca
  readonly focusout = (e: FocusEvent) => {
    if (!(this.wrapper!.nativeElement as HTMLElement).contains(e.relatedTarget as HTMLElement)  // lost focus
       && (e.target !== document.activeElement)) {  // not focused on other windows
        this.inputHidden = true;
        this.searchExpr$.next('');
    }
  }

  keyup$ = new Subject<KeyboardEvent>();
  searchExpr$ = new Subject<string>();
  resetCategory$ = new Subject<void>();
  destroy$: Subject<void>;

  availableTags: Tag[];
  selectedTags: Tag[];
  results$: Subject<any[]>;

  constructor(private api: MasterApiService) {
    this.inputHidden = true;
    this.availableTags = [];
    this.selectedTags = [];
    this.destroy$ = new Subject();
    this.results$ = new Subject();
    this.api.resources.configuration.tags.getAll().pipe(takeUntil(this.destroy$)).subscribe(tags => this.availableTags = tags);
    this.keyup$.pipe(
      takeUntil(this.destroy$),
      map(e => (e.target as HTMLInputElement).value),   // Obtenim el valor del input
      debounceTime(800),                                // Últim valor després de 800ms
      map(t => (t.length < 2) ? '' : t),                // Si té menys de 2 caràcters, no fem res
    ).subscribe(expr =>  this.searchExpr$.next(expr));

    this.searchExpr$.pipe(
      takeUntil(this.destroy$),
      startWith(''),
      pairwise(),                                       // Si el valor anterior és igual al nou,
      filter(([prev, curr]) => prev !== curr),          // no fem res
      map(([_prev, curr]) => curr)                      // Obtenim el valor actual
    ).subscribe(expr => {
      this.resetCategory$.next();
      this.search(expr);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // AggregateTypIds
  ORGANIZ_ID = 3;
  PRODUCT_ID = 4;
  PERSONS_ID = 6;

  // Max results by type
  MAX_ORGANIZ = 10;
  MAX_PERSONS = 10;
  MAX_PRODUCT = 10;
  MAX_ORGANIZ_RELATEDWITHPERSON = 5;
  MAX_PERSONS_RELATEDWITHORGANI = 5;

  async search(text: string):Promise<void> {
    const results:any[] = [];

    if (text === '') {
      this.results$.next(results);
      return;
    }
    
    console.log(`searching: ${text}`);

    IdStore.clear();
    const searches:any[] = await this.GetSearches(text);

    // ORGANIZATIONS, PERSONS, AND PRODUCTS
    for (let i = 0; i < searches.length; i++)
      this.AddResult(results, await this.ProcessSearch(searches[i]));

    // ORGANIZATIONS RELATED WITH PERSONS
    const personsResults = results.filter(r => r.aggregateTypeId === this.PERSONS_ID);
    if (personsResults.length > 0) {
      const relatedOrganizations = await this.GetOrganizationsRelatedWithPersons(personsResults);
      relatedOrganizations.forEach(organization => this.AddResult(results, organization));
    }

    // PERSONS RELATED WITH ORGANIZATIONS
    const organizResults = results.filter(r => r.aggregateTypeId === this.ORGANIZ_ID);
    if (organizResults.length > 0) {
      const relatedPersons = await this.GetPersonsRelatedWithOrganizations(organizResults);
      relatedPersons.forEach(person => this.AddResult(results, person));
    } 
  }

  private async GetSearches(criteria:string):Promise<any[]> {
    const searches = await firstValueFrom(this.api.search({search: criteria, p: this.GetPagination(), companyId: this.api.auth.userValue?.lastCompanyId }));
    return this.LimitSearches(searches);
  }
  
  private LimitSearches(searches:any[]):any[] {
    const limitedSearches:any[] = [];

    let contOrganiz = 0;
    let contPersons = 0;
    let contProduct = 0;
    let include = false;

    for (let i = 0; i < searches.length; i++) {
      switch(searches[i].aggregateTypeId) {
        
        case this.ORGANIZ_ID: 
          include = contOrganiz < this.MAX_ORGANIZ;
          if (include) contOrganiz++;
          break;

        case this.PERSONS_ID:
          include = contPersons < this.MAX_PERSONS;
          if (include) contPersons++;
          break;

        case this.PRODUCT_ID:
          include = contProduct < this.MAX_PRODUCT;
          if (include) contProduct++;
          break;
      }

      if (include) limitedSearches.push(searches[i]);
    }

    return limitedSearches;
  }

  private AddResult(results:any[], result:any):void {
    if (!IdStore.existsId(result.id)) {
      results.push(result);
      this.results$.next(results);
      IdStore.includeId(result.id);
    }
  }

  private async ProcessSearch(search:any):Promise<any> {
    let resource:GenericResource<any>;

    switch (search.aggregateTypeId) {
      case this.ORGANIZ_ID: resource = this.api.resources.organization.organizations;  break;
      case this.PERSONS_ID: resource = this.api.resources.organization.people;         break;
      case this.PRODUCT_ID: resource = this.api.resources.product.products;            break;
      default:
        throw new Error('Not valid aggregateTypeId');
    }
   
    const item = await firstValueFrom(resource.get(search.sourceId));
    return {
      aggregateTypeId: search.aggregateTypeId,
      ...item,
    };
  }

  private async GetOrganizationsRelatedWithPersons(persons:any[]):Promise<any[]> {
    const organizations:any[] = [];

    for (let i = 0; i < persons.length; i++) {
      if (organizations.length === this.MAX_ORGANIZ_RELATEDWITHPERSON) break;

      const personId = persons[i].id;
      const personName = persons[i].name;
      const personOrgs = await firstValueFrom(this.api.resources.organization.organizations.getAll({ personId: personId }));

      personOrgs.forEach(org => {
        organizations.push({
          aggregateTypeId: this.ORGANIZ_ID,
          sourceId: org.id,
          parent: { 
            aggregateTypeId: this.PERSONS_ID,
            id: personId,
            description: personName 
          },
          ...org,
        });
      });
    };

    return organizations;
  }

  private async GetPersonsRelatedWithOrganizations(organizations:any[]):Promise<any[]> {
    const persons:any[] = [];

    for (let i = 0; i < organizations.length; i++) {
      if (persons.length === this.MAX_PERSONS_RELATEDWITHORGANI) break;

      const organizationId = organizations[i].id;
      const contacts: OrganizationContact[] = await firstValueFrom(this.api.getOrganizationContacts(organizationId));

      for (let j = 0; j < contacts.length; j++) {
        if (persons.length === this.MAX_PERSONS_RELATEDWITHORGANI) break;

        const personId = contacts[j].personId;
        const person = await firstValueFrom(this.api.resources.organization.people.get(personId));

        persons.push({
          aggregateTypeId: this.PERSONS_ID,
          sourceId: personId,
          parent: {
            aggregateTypeId: this.ORGANIZ_ID,
            id: organizationId,
            description: organizations[i].legalName
          },
          ...person
        });
      }

    }

    return persons;
  }

  private GetPagination():string {
    const options: IGenericListOptions = { pagination: { page: 1, size: 30 } };

    let pagination = '';
    if (options.pagination !== undefined)
      pagination = [options.pagination.page, options.pagination.size].join(',');

    return pagination;
  }

}

class IdStore {
  private static ids: string[] = [];

  static clear():void {
    this.ids = [];
  }

  static includeId(id:string):void {
    this.ids.push(id);
  }

  static existsId(id:string):boolean {
    return this.ids.includes(id);
  }
}